import React, { useContext, useMemo } from 'react';
import { DealerContext } from '../../context';
import type { HeaderState } from '../../reducers/header-reducer';
import { dealerSearchParams } from '../../utils/nav-utils';
import AudiHeaderDealerContext from '../AudiHeaderDealerContext/AudiHeaderDealerContext';
import AudiHeaderDealerContextAlpha from '../AudiHeaderDealerContext/AudiHeaderDealerContext_alpha';
import DealerSearch from '../AudiHeaderDealerContext/DealerSearch';

interface DealerComponentsProps {
  headerState: HeaderState;
  showDealerContext: boolean;
  headerNavigationItemsAmount: number;
  dealerSearch: boolean;
  isTierOneDomain: boolean;
  dealerSearchGeolocationEnabled: boolean;
  onClickDealerContext: () => void;
  onClickDealerSearchContext: () => void;
  isSmallView: boolean;
}

const DealerComponents: React.FC<DealerComponentsProps> = ({
  headerState,
  showDealerContext,
  headerNavigationItemsAmount,
  dealerSearch,
  isTierOneDomain,
  dealerSearchGeolocationEnabled,
  onClickDealerContext,
  onClickDealerSearchContext,
  isSmallView,
}) => {
  const { autoConnectedUnsubscribed, geoError, dealerData, dealerConnectedNotification } =
    useContext(DealerContext);
  const IS_LEGACY = Boolean(process.env.IS_LEGACY);
  const hasDealerSearchParams = useMemo(() => dealerSearchParams(), []);

  const componentType = useMemo(() => {
    const isDealerContextLegacy = showDealerContext && isTierOneDomain && IS_LEGACY;
    const isDealerSearchLegacyDesktop =
      dealerSearch && isTierOneDomain && IS_LEGACY && !isSmallView;
    const isDealerSearchAlphaDesktop =
      dealerSearch && isTierOneDomain && !IS_LEGACY && !isSmallView;
    const isDealerContextAlpha = showDealerContext && isTierOneDomain && !IS_LEGACY;

    return {
      isDealerContextLegacy,
      isDealerSearchLegacyDesktop,
      isDealerSearchAlphaDesktop,
      isDealerContextAlpha,
    };
  }, [showDealerContext, dealerSearch, isTierOneDomain, isSmallView]);

  const gelocationEnabled = useMemo(
    () => dealerSearchGeolocationEnabled && !autoConnectedUnsubscribed && !geoError,
    [dealerSearchGeolocationEnabled, autoConnectedUnsubscribed, geoError],
  );

  const showDealerSearch = useMemo(
    () =>
      gelocationEnabled ||
      headerState.activeItem.showDealerSearch ||
      hasDealerSearchParams ||
      dealerSearch,
    [
      gelocationEnabled,
      headerState.activeItem.showDealerSearch,
      hasDealerSearchParams,
      dealerSearch,
    ],
  );

  const isDealerSearchAlphaOptIn = useMemo(
    () =>
      componentType.isDealerSearchAlphaDesktop &&
      dealerData &&
      !hasDealerSearchParams &&
      !dealerConnectedNotification?.isConnected &&
      dealerConnectedNotification?.geoLocationOption === 'optIn',
    [
      componentType.isDealerSearchAlphaDesktop,
      dealerData,
      hasDealerSearchParams,
      dealerConnectedNotification,
    ],
  );

  // Legacy DealerContext Desktop View
  if (componentType.isDealerContextLegacy) {
    return (
      <AudiHeaderDealerContext
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        dealerSearch={dealerSearch}
        onClickDealerContext={onClickDealerContext}
        onClickDealerSearchContext={onClickDealerSearchContext}
        showDealerAdditionalData={headerState.activeItem.showDealer}
        showDealerSearch={showDealerSearch}
      />
    );
  }

  // Legacy DealerSearch Desktop View
  if (componentType.isDealerSearchLegacyDesktop) {
    return (
      <DealerSearch
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        onClickDealerSearchContext={onClickDealerSearchContext}
        dealerSearchGeolocationEnabled={gelocationEnabled}
        showDealerSearch={showDealerSearch}
        openDealerDetails={headerState.activeItem.openDealerDetails}
      />
    );
  }

  // Alpha DealerSearch Desktop View - optIn
  if (isDealerSearchAlphaOptIn) {
    return (
      <DealerSearch
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        onClickDealerSearchContext={onClickDealerSearchContext}
        dealerSearchGeolocationEnabled={false}
        showDealerSearch={true}
        openDealerDetails={headerState.activeItem.openDealerDetails}
      />
    );
  }

  // Alpha DealerContext Desktop View
  if (componentType.isDealerContextAlpha) {
    return (
      <AudiHeaderDealerContextAlpha
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        dealerSearch={dealerSearch}
        onClickDealerContext={onClickDealerContext}
        onClickDealerSearchContext={onClickDealerSearchContext}
        showDealerAdditionalData={headerState.activeItem.showDealer}
        isMobileView={isSmallView}
        showDealerSearch={showDealerSearch}
      />
    );
  }

  // Alpha DealerSearch Desktop View
  if (componentType.isDealerSearchAlphaDesktop) {
    return (
      <DealerSearch
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        onClickDealerSearchContext={onClickDealerSearchContext}
        dealerSearchGeolocationEnabled={gelocationEnabled}
        showDealerSearch={showDealerSearch}
        openDealerDetails={headerState.activeItem.openDealerDetails}
      />
    );
  }

  return null;
};

export default DealerComponents;
